import { assetUrl } from '../../../lib/assets';
import {
  defineScenario,
  act,
  beat,
  gp,
  infra,
  at,
  PACING,
  establishingWide,
  dronePushIn,
  orbit180,
  craneReveal,
  majesticPullback,
  hudEvent,
  hudTeam,
  hudRegion,
  notification,
  productScreen,
  panel,
  updatePanel,
  removePanel,
  spawnVehicle,
  dimStart,
  dimEnd,
  hold,
} from '../authoring';
import type { ScenarioStep } from '../../core/types';

// ──────────────────────────────────────────────────────────────────────────
// SCENARIO 1 — FRONTIER MAINS REHAB & PLANNED WORKS
//
// Single scheme: Cardwell St in a suburban residential area.
// The dimming starts from ACT 1. Houses are progressively undimmed as
// letters/comms reach them (via highlight-buildings step). During works
// the focus shifts to the excavation holes and pipe. At reinstatement
// everything undims.
//
// The customer lettering is the centrepiece — camera stays low on houses,
// each row of properties lights up as the comms ring expands past them,
// and welfare steps are shown sequentially in the HUD.
// ──────────────────────────────────────────────────────────────────────────

// ─── Site (suburban — surrounded by houses) ─────────────────────────────────
const CARDWELL_ST = infra('mains-renewal-st', gp(-22, 18));
const DEPOT_SOUTH = infra('depot-south', gp(0, 30));

// Excavation holes along the street
const HOLES: Array<{ coords: [number, number]; id: string }> = [
  { coords: gp(-24, 18), id: 'hole-1' },
  { coords: gp(-22, 18), id: 'hole-2' },
  { coords: gp(-20, 18), id: 'hole-3' },
  { coords: gp(-18, 18), id: 'hole-4' },
];

// House positions — north row and south row either side of the road
const HOUSES_NORTH: [number, number][] = [
  gp(-25, 16), gp(-23, 16), gp(-21, 16), gp(-19, 16), gp(-17, 16),
];
const HOUSES_SOUTH: [number, number][] = [
  gp(-25, 20), gp(-23, 20), gp(-21, 20), gp(-19, 20), gp(-17, 20),
];
const ALL_HOUSES: [number, number][] = [...HOUSES_NORTH, ...HOUSES_SOUTH];

// Pipe underground along the trench
const PIPE_PATH: [number, number][] = [
  gp(-25, 18), gp(-23, 18), gp(-21, 18), gp(-19, 18), gp(-17, 18),
];

// Helper: highlight buildings near a coordinate (undim houses)
function highlightAt(coords: [number, number], radius = 60): ScenarioStep {
  return { type: 'highlight-buildings', duration: 0, params: { coordinates: coords, radius } };
}

export const streetWorksScenario = defineScenario(
  {
    id: 'street-works',
    title: 'Holistic Delivery of Mains Replacement/Rehabilitation',
    description: 'End-to-end MRP — customer lettering to houses, multi-hole excavation, pipe-lay, VYN compliance, reinstatement',
    hudLabel: 'MRP LIVE',
    telemetry: 'street-works',
    activeInfraIds: ['mains-renewal-st', 'depot-south'],
  },
  [
    // ═══════════════════════════════════════════════════════════════════════
    // ACT 1: ESTABLISHING — Dim from the start, show the street (0–15s)
    // Everything dimmed. Only the scheme marker visible. Sets the mood.
    // ═══════════════════════════════════════════════════════════════════════
    act('Establish', [
      dimStart,

      beat(PACING.ESTABLISH, [
        establishingWide(CARDWELL_ST, { bearing: 0, pitch: 48, zoom: 15.5 }),
        hudEvent('programme', 'MRP LIVE', 'Cardwell St mains renewal — 20 residential properties. Before work begins.', 'info'),
        hudRegion([
          { name: 'Cardwell St Residents', status: 'monitoring', properties: 20 },
        ]),
        { type: 'hud-metric', duration: 0, params: { id: 'properties', kind: 'counter', label: 'Properties', value: 20, unit: '', severity: 'info' } },
      ]),

      productScreen('Control Room — MRP Live', {
        image: assetUrl('/screens/mrp-live-control-room.png'),
        device: 'desktop',
        subtitle: '277 active projects — PSR welfare monitoring live',
        role: 'Programme Manager',
        url: 'app.skewb.io/mrp/control-room',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 2: CUSTOMER LETTERING — Houses light up progressively (15–65s)
    // Camera low on the residential street. Comms ring expands outward.
    // As it reaches each row of houses, those houses UNDIM (highlight).
    // Welfare steps shown one by one. This is the centrepiece.
    // ═══════════════════════════════════════════════════════════════════════
    act('Customer Lettering', [
      // Camera settles low on the street — looking at dimmed houses
      beat(PACING.PUSH_IN, [
        { type: 'camera', duration: PACING.PUSH_IN, params: { center: gp(-22, 18), infraId: 'mains-renewal-st', zoom: 17, pitch: 42, bearing: -10 } },
        hudEvent('comms', 'CUSTOMER PLAN', 'Beginning customer lettering for 20 properties. Letters, welfare calls, gas notices.', 'info'),
        { type: 'hud-metric', duration: 0, params: { id: 'letters', kind: 'counter', label: 'Letters Sent', value: 0, unit: '/ 20', severity: 'warning' } },
      ]),

      // WAVE 1: Letters sent — comms ring expands, first houses undim
      // Camera orbits slowly so you see the houses lighting up
      beat(6000, [
        { type: 'camera', duration: 6000, params: { center: gp(-23, 17), zoom: 17.2, pitch: 40, bearing: 15 } },
        { type: 'comms-broadcast', duration: 6000, params: {
          infraId: 'mains-renewal-st',
          coordinates: gp(-22, 18),
          color: '#10b981',
          radius: 100,
          targets: HOUSES_NORTH.slice(0, 3),
          id: 'letter-wave-1',
        }},
        // Undim the first houses as ring reaches them
        highlightAt(gp(-25, 16), 50),
        highlightAt(gp(-23, 16), 50),
        highlightAt(gp(-21, 16), 50),
        notification('LETTERS SENT — NORTH ROW', '3 properties lettered. Works notification with start date, duration, contact details.', 'success', PACING.BEAT),
        { type: 'hud-metric', duration: 0, params: { id: 'letters', kind: 'counter', label: 'Letters Sent', value: 3, unit: '/ 20', severity: 'warning' } },
      ]),

      // More houses — east end of north row
      beat(5000, [
        { type: 'camera', duration: 5000, params: { center: gp(-19, 17), zoom: 17.2, pitch: 40, bearing: 30 } },
        highlightAt(gp(-19, 16), 50),
        highlightAt(gp(-17, 16), 50),
        { type: 'hud-metric', duration: 0, params: { id: 'letters', kind: 'counter', label: 'Letters Sent', value: 5, unit: '/ 20', severity: 'warning' } },
        hudEvent('letter', 'NORTH ROW COMPLETE', '5 properties on north side lettered. Moving to south side.', 'info'),
      ]),

      // WAVE 2: South row of houses
      beat(6000, [
        { type: 'camera', duration: 6000, params: { center: gp(-22, 19), zoom: 17.2, pitch: 40, bearing: -15 } },
        { type: 'comms-broadcast', duration: 6000, params: {
          infraId: 'mains-renewal-st',
          coordinates: gp(-22, 18),
          color: '#10b981',
          radius: 140,
          targets: HOUSES_SOUTH,
          id: 'letter-wave-2',
        }},
        // Undim south row
        highlightAt(gp(-25, 20), 50),
        highlightAt(gp(-23, 20), 50),
        highlightAt(gp(-21, 20), 50),
        highlightAt(gp(-19, 20), 50),
        highlightAt(gp(-17, 20), 50),
        { type: 'hud-metric', duration: 0, params: { id: 'letters', kind: 'counter', label: 'Letters Sent', value: 10, unit: '/ 20', severity: 'warning' } },
        notification('LETTERS SENT — SOUTH ROW', '5 more properties lettered. Total: 10 of 20.', 'success', PACING.BEAT),
      ]),

      // Remaining properties (wider radius captures further houses)
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(-22, 18), infraId: 'mains-renewal-st', zoom: 16.5, pitch: 45, bearing: 0 } },
        highlightAt(gp(-22, 18), 200),
        { type: 'hud-metric', duration: 0, params: { id: 'letters', kind: 'counter', label: 'Letters Sent', value: 20, unit: '/ 20', severity: 'success' } },
        hudEvent('letter', 'ALL LETTERS SENT', 'All 20 properties on Cardwell St have been lettered.', 'success'),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'letter-wave-1' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'letter-wave-2' } } as ScenarioStep,

      // WELFARE CALLS — separate beat with team tracker
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(-23, 16), zoom: 17.5, pitch: 38, bearing: 20 } },
        hudEvent('welfare', 'PRE-CARE WELFARE', 'Welfare calls to 6 vulnerable customers. Priority service confirmed.', 'success'),
        hudTeam([
          { name: 'Letter All Properties', status: 'complete', destination: '20 / 20' },
          { name: 'Pre-Care Welfare Calls', status: 'active', destination: '6 vulnerable' },
          { name: 'Gas Restoration Notice', status: 'pending', destination: '20 properties' },
          { name: 'Post-Care Walk Off', status: 'pending', destination: 'After works' },
        ]),
        { type: 'hud-metric', duration: 0, params: { id: 'welfare', kind: 'counter', label: 'Welfare Calls', value: 6, unit: '/ 6', severity: 'success' } },
      ]),

      // GAS RESTORATION — blue comms wave
      beat(6000, [
        orbit180(CARDWELL_ST, { startBearing: 20, arc: 80, zoom: 16.5, pitch: 45, duration: 6000 }),
        { type: 'comms-broadcast', duration: 6000, params: {
          infraId: 'mains-renewal-st',
          coordinates: gp(-22, 18),
          color: '#0ea5e9',
          radius: 200,
          targets: ALL_HOUSES,
          id: 'gas-notice',
        }},
        hudEvent('gas', 'GAS RESTORATION NOTICE', 'Gas restoration sent to all 20 properties. No overnight interruption confirmed.', 'info'),
        hudTeam([
          { name: 'Letter All Properties', status: 'complete', destination: '20 / 20' },
          { name: 'Pre-Care Welfare Calls', status: 'complete', destination: '6 / 6' },
          { name: 'Gas Restoration Notice', status: 'complete', destination: '20 / 20' },
          { name: 'Post-Care Walk Off', status: 'pending', destination: 'After works' },
        ]),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'gas-notice' } } as ScenarioStep,

      // Summary
      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(-22, 18), infraId: 'mains-renewal-st', zoom: 16.5, pitch: 48, bearing: -5 } },
        panel('letter-summary', 'Customer Plan — Complete', 'data', CARDWELL_ST, [
          { label: 'Letters Sent', value: '20 / 20', status: 'good' },
          { label: 'Welfare Calls', value: '6 / 6', status: 'good' },
          { label: 'Gas Notices', value: '20 / 20', status: 'good' },
          { label: 'CSAT Predicted', value: '8.5', status: 'good' },
        ]),
        notification('CUSTOMER PLAN COMPLETE', 'All lettering done. Scheme ready to begin works.', 'success', PACING.BEAT),
      ]),

      removePanel('letter-summary'),

      productScreen('Customer Plan — Before Work Begins', {
        image: assetUrl('/screens/customer-plan-lettering.png'),
        device: 'desktop',
        subtitle: '56 properties affected — lettering, welfare, gas restoration complete',
        role: 'Customer Manager',
        url: 'app.skewb.io/mrp/customer-plan/cardwell-st',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 3: WORKS BEGIN — Multiple holes open, pipe underground (65–100s)
    // ═══════════════════════════════════════════════════════════════════════
    act('Works Begin', [
      // Open all 4 excavation holes
      ...HOLES.map((h) => ({
        type: 'excavation' as const,
        duration: 6000,
        params: { coordinates: h.coords, radius: 12, id: h.id },
      })),

      beat(PACING.PUSH_IN, [
        dronePushIn(CARDWELL_ST, { bearing: -30, zoom: 17 }),
        hudEvent('works', 'WORKS STARTED', '4 excavation holes opened along Cardwell St. Pipe-lay commencing.', 'warning'),
        { type: 'hud-metric', duration: 0, params: { id: 'holes', kind: 'counter', label: 'Holes Open', value: 4, unit: '', severity: 'warning' } },
        hudTeam([
          { name: 'Gang Alpha', status: 'on-site', destination: 'Holes 1-2' },
          { name: 'Gang Bravo', status: 'on-site', destination: 'Holes 3-4' },
        ]),
      ]),

      // Pipe-lay underground between holes
      beat(9000, [
        orbit180(CARDWELL_ST, { startBearing: -30, arc: 160, zoom: 17.5, pitch: 72, duration: 9000 }),
        { type: 'pipe-lay', duration: 9000, params: { path: PIPE_PATH, color: '#38bdf8', id: 'pipe-main' } },
        panel('works-panel', 'Live Works — Cardwell St', 'infrastructure', CARDWELL_ST, [
          { label: 'Holes', value: '4 open', status: 'warning' },
          { label: 'Pipe Progress', value: '0m → 65m' },
          { label: 'S74 Permit', value: 'Active', status: 'warning' },
          { label: 'Defects', value: '0' },
        ]),
      ]),

      removePanel('works-panel'),

      productScreen('Manage Sites & Excavations', {
        image: assetUrl('/screens/vyn-daily-overdue.png'),
        device: 'desktop',
        subtitle: 'Daily VYN calendar — excavation site management',
        role: 'Site Manager',
        url: 'app.skewb.io/mrp/scheme/sites-excavations',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 4: VYN & ISSUES — Problem + resolution (100–130s)
    // ═══════════════════════════════════════════════════════════════════════
    act('VYN & Issues', [
      beat(PACING.PUSH_IN, [
        dronePushIn(CARDWELL_ST, { bearing: 60, zoom: 17.5 }),
        hudEvent('alert', 'VYN FAILURE', 'VYN audit failed at Hole 3 — TM non-compliant. 4-hour defect started.', 'critical'),
        { type: 'hud-metric', duration: 0, params: { id: 'defect', kind: 'countdown', label: '4-Hour Defect', value: 180, unit: 'min', severity: 'critical' } },
      ]),

      spawnVehicle('sw-safety', 'kenney-van', DEPOT_SOUTH, at(-20, 17), 'Safety', 10000),

      beat(PACING.ORBIT, [
        orbit180(CARDWELL_ST, { startBearing: 60, arc: 120, zoom: 17, pitch: 68 }),
        panel('vyn-panel', 'VYN Action Card — Hole 3', 'data', CARDWELL_ST, [
          { label: 'Issue', value: 'TM Non-Compliant', status: 'critical' },
          { label: 'Action', value: 'Barriers Reposition', status: 'warning' },
          { label: 'Raised By', value: 'Back Office' },
          { label: 'Re-Audit', value: 'Pending', status: 'warning' },
        ]),
      ]),

      beat(PACING.BEAT, [
        { type: 'camera', duration: PACING.BEAT, params: { center: gp(-20, 18), zoom: 17.5, pitch: 70, bearing: 0 } },
        updatePanel('vyn-panel', [
          { label: 'Issue', value: 'RESOLVED', status: 'good' },
          { label: 'Corrective Action', value: 'Complete', status: 'good' },
          { label: 'VYN Re-Audit', value: 'PASSED', status: 'good' },
          { label: 'Site', value: 'Compliant', status: 'good' },
        ]),
        notification('VYN PASSED', 'Corrective works complete. Site compliant.', 'success', PACING.BEAT),
        { type: 'clear-effect', duration: 0, params: { id: 'defect' } },
      ]),

      removePanel('vyn-panel'),

      productScreen('Daily VYNs — Submitted & Reviewed', {
        image: assetUrl('/screens/vyn-daily-submitted.png'),
        device: 'desktop',
        subtitle: 'VYN submissions with AI input — corrective action verified',
        role: 'Safety Manager',
        url: 'app.skewb.io/mrp/vyn/daily',
      }),
    ]),

    // ═══════════════════════════════════════════════════════════════════════
    // ACT 5: REINSTATED + POST-CARE (130–165s)
    // Holes cleared. Undim everything. Post-care comms wave to houses.
    // ═══════════════════════════════════════════════════════════════════════
    act('Reinstated', [
      // Clear all holes
      ...HOLES.map((h) => ({
        type: 'clear-effect' as const,
        duration: 0,
        params: { id: h.id },
      })),
      { type: 'clear-effect', duration: 0, params: { id: 'pipe-main' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'holes' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'letters' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'welfare' } } as ScenarioStep,
      { type: 'clear-effect', duration: 0, params: { id: 'properties' } } as ScenarioStep,

      dimEnd,

      beat(PACING.PUSH_IN, [
        craneReveal(CARDWELL_ST, { zoom: 16, bearing: 10 }),
        hudEvent('complete', 'REINSTATED', 'All 4 holes closed. Surface to HAUC spec. Road reopened.', 'success'),
        notification('REINSTATEMENT COMPLETE', '4 holes closed. Tarmac restored. Road markings done.', 'success', PACING.BEAT),
      ]),

      // Post-care walk-off — final comms wave (houses fully visible now)
      beat(10000, [
        orbit180(CARDWELL_ST, { startBearing: 10, arc: 160, zoom: 16, pitch: 48, duration: 10000 }),
        { type: 'comms-broadcast', duration: 10000, params: {
          infraId: 'mains-renewal-st',
          coordinates: gp(-22, 18),
          color: '#10b981',
          radius: 220,
          targets: ALL_HOUSES,
          id: 'post-care',
        }},
        hudEvent('comms', 'POST-CARE WALK OFF', 'Final welfare check sent to all 20 properties. Works complete confirmed.', 'success'),
        hudTeam([
          { name: 'Letter All Properties', status: 'complete', destination: '20 / 20' },
          { name: 'Pre-Care Welfare', status: 'complete', destination: '6 / 6' },
          { name: 'Gas Restoration', status: 'complete', destination: '20 / 20' },
          { name: 'Post-Care Walk Off', status: 'complete', destination: '20 / 20' },
        ]),
        panel('final-panel', 'Scheme Complete', 'data', CARDWELL_ST, [
          { label: 'Holes Reinstated', value: '4 / 4', status: 'good' },
          { label: 'S74 Penalty', value: '£0', status: 'good' },
          { label: 'Customer CSAT', value: '9.1 / 10', status: 'good' },
          { label: 'VYN Status', value: 'Compliant', status: 'good' },
        ]),
      ]),

      { type: 'clear-effect', duration: 0, params: { id: 'post-care' } } as ScenarioStep,
      removePanel('final-panel'),

      productScreen('Customer Sensitivity — Geospatial', {
        image: assetUrl('/screens/customer-sensitivity-geospatial.png'),
        device: 'desktop',
        subtitle: 'Overall satisfaction 8.94 — communication 8.87, notification 9.14',
        role: 'Performance Manager',
        url: 'app.skewb.io/performance/customer-sensitivity',
      }),

      { type: 'color-grade', duration: 0, params: { tint: '#fbbf24', intensity: 0.08, vignette: 0.2 } } as ScenarioStep,

      beat(PACING.PULLBACK, [
        majesticPullback({ bearing: -20 }),
        notification('MRP DELIVERED', 'Cardwell St — 20 properties. Zero penalty. 100% lettering. VYN compliant.', 'success'),
      ]),

      { type: 'color-grade', duration: 0, params: { tint: 'transparent', intensity: 0, vignette: 0 } } as ScenarioStep,
      hold(PACING.HOLD),
    ]),
  ],
);
